import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Bed, Bath, Maximize, MapPin, ArrowLeft, ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';
import SEO from '../components/SEO';
import PropertyGallery from '../components/PropertyGallery';
import PropertyLeadForm from '../components/PropertyLeadForm';
import MortgageCalculator from '../components/MortgageCalculator';
import { InlineCTA, StickyCTA, LeadModal } from '../components/PropertyContactCTA';
import { useLanguage } from '../context/LanguageContext';
import { readListingState } from '../utils/listingState';

/**
 * Bottom prev/next bar — 3 zones (prev · center · next) on desktop, stacks on
 * mobile. Center button is a shortcut back to the filtered listing for people
 * who scrolled all the way but don't want to jump into another property.
 */
const BottomPrevNext = ({ prevId, nextId, currentIndex, total, isNL, onPrev, onNext, onBack }) => (
  <div style={bpn.wrap} data-testid="property-prev-next-bottom">
    <button
      type="button"
      onClick={onPrev}
      disabled={!prevId}
      style={prevId ? bpn.side : bpn.sideDisabled}
      data-testid="property-prev-bottom"
    >
      <ChevronLeft size={18} />
      <div style={bpn.sideText}>
        <span style={bpn.sideLabel}>{isNL ? 'Vorige woning' : 'Previous property'}</span>
        {prevId && <span style={bpn.sideId}>#{prevId}</span>}
      </div>
    </button>

    <button
      type="button"
      onClick={onBack}
      style={bpn.center}
      data-testid="property-back-bottom"
    >
      <ArrowLeft size={16} />
      <span>{isNL ? 'Terug naar resultaten' : 'Back to results'}</span>
      <span style={bpn.centerCounter}>{currentIndex + 1} / {total}</span>
    </button>

    <button
      type="button"
      onClick={onNext}
      disabled={!nextId}
      style={nextId ? bpn.side : bpn.sideDisabled}
      data-testid="property-next-bottom"
    >
      <div style={{ ...bpn.sideText, textAlign: 'right' }}>
        <span style={bpn.sideLabel}>{isNL ? 'Volgende woning' : 'Next property'}</span>
        {nextId && <span style={bpn.sideId}>#{nextId}</span>}
      </div>
      <ChevronRight size={18} />
    </button>
  </div>
);

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalCta, setModalCta] = useState(null); // null = closed; 'info'/'viewing'/'callback' = open
  const { language, t } = useLanguage();

  /* Read the persisted listing context ONCE per id change. Gives us the
     filtered-and-sorted id list so we can offer prev/next in the same order
     the visitor sees on /woningen. Re-reads when `id` changes so navigating
     via the prev/next buttons keeps the context fresh. */
  const listCtx = useMemo(() => readListingState(), [id]);
  const filteredIds = listCtx?.filteredIds || [];
  const currentIndex = filteredIds.indexOf(id);
  const prevId = currentIndex > 0 ? filteredIds[currentIndex - 1] : null;
  const nextId = currentIndex >= 0 && currentIndex < filteredIds.length - 1
    ? filteredIds[currentIndex + 1]
    : null;
  const hasListContext = currentIndex !== -1;

  const openModal = (cta) => setModalCta(cta || 'info');
  const closeModal = () => setModalCta(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/xml/property/${id}?lang=${language}`)
      .then(res => {
        if (!res.ok) throw new Error('Not found');
        return res.json();
      })
      .then(data => {
        setProperty(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id, language]);

  /* "Back to results" — navigate directly to /woningen (never history.back)
     so it also works cleanly after the visitor used the Prev/Next buttons,
     which each add history entries. Properties.js will restore filters +
     scroll position from sessionStorage on mount. */
  const backToResults = () => {
    navigate('/woningen');
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat(language === 'nl' ? 'nl-NL' : 'en-GB', {
      style: 'currency', currency: 'EUR', minimumFractionDigits: 0,
    }).format(price);

  if (loading) {
    return (
      <div style={styles.page}><div style={styles.container}>
        <p style={styles.loadingText}>{t('common.loading')}</p>
      </div></div>
    );
  }

  if (!property) {
    return (
      <div style={styles.page}><div style={styles.container}>
        <p style={styles.errorText}>{language === 'nl' ? 'Woning niet gevonden' : 'Property not found'}</p>
        <Link to="/woningen" style={styles.backLink}>
          <ArrowLeft size={20} />
          {t('property.backToProperties')}
        </Link>
      </div></div>
    );
  }

  const realEstateSchema = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: property.title,
    description: (property.description || '').substring(0, 500),
    url: typeof window !== 'undefined' ? window.location.href : '',
    image: property.images?.[0],
    datePosted: property.date_listed,
    offers: {
      '@type': 'Offer',
      price: property.price,
      priceCurrency: 'EUR',
      availability: 'https://schema.org/InStock',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: property.location,
      addressRegion: property.region,
      addressCountry: 'ES',
    },
    numberOfRooms: property.bedrooms,
    numberOfBathroomsTotal: property.bathrooms,
    floorSize: { '@type': 'QuantitativeValue', value: property.surface, unitCode: 'MTK' },
  };

  return (
    <div style={styles.page}>
      <SEO
        title={`${property.title} – €${(property.price || 0).toLocaleString('nl-NL')}`}
        description={`${property.title} in ${property.location}, ${property.region}. ${property.bedrooms} slk · ${property.bathrooms} bdk · ${property.surface}m². ${(property.description || '').substring(0, 100)}`}
        image={property.images?.[0]}
      />
      <script type="application/ld+json">{JSON.stringify(realEstateSchema)}</script>

      <div style={styles.container}>
        {/* Contextual back button — reads richer copy when we know the visitor
            came from a filtered listing. `backToResults()` uses history.back()
            so the browser's scroll restoration + our sessionStorage-based
            filter/scroll restore fire in the right order. */}
        <div style={styles.topNavRow}>
          <button
            type="button"
            onClick={backToResults}
            style={styles.backButton}
            data-testid="back-link"
          >
            <ArrowLeft size={18} />
            {hasListContext
              ? (language === 'nl' ? 'Terug naar resultaten' : 'Back to results')
              : t('property.backToProperties')}
            {hasListContext && (
              <span style={styles.backPosition}>
                {currentIndex + 1} / {filteredIds.length}
              </span>
            )}
          </button>

          {/* Prev/Next controls — only shown when we know the ordered list */}
          {hasListContext && (filteredIds.length > 1) && (
            <div style={styles.prevNext} data-testid="property-prev-next">
              <button
                type="button"
                onClick={() => prevId && navigate(`/woning/${prevId}`)}
                disabled={!prevId}
                style={prevId ? styles.prevNextBtn : styles.prevNextBtnDisabled}
                aria-label={language === 'nl' ? 'Vorige woning' : 'Previous property'}
                data-testid="property-prev"
              >
                <ChevronLeft size={16} />
                <span style={styles.prevNextLabel}>
                  {language === 'nl' ? 'Vorige' : 'Previous'}
                </span>
              </button>
              <button
                type="button"
                onClick={() => nextId && navigate(`/woning/${nextId}`)}
                disabled={!nextId}
                style={nextId ? styles.prevNextBtn : styles.prevNextBtnDisabled}
                aria-label={language === 'nl' ? 'Volgende woning' : 'Next property'}
                data-testid="property-next"
              >
                <span style={styles.prevNextLabel}>
                  {language === 'nl' ? 'Volgende' : 'Next'}
                </span>
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>

        <PropertyGallery images={property.images} alt={property.title} />

        <div style={styles.layout}>
          <div style={styles.main}>
            <div style={styles.header}>
              <div>
                <h1 style={styles.title} data-testid="property-detail-title">{property.title}</h1>
                <div style={styles.locationRow}>
                  <MapPin size={18} color="#8C8B88" />
                  <span style={styles.location}>{property.location}, {property.region}</span>
                </div>
                {property.ref && (
                  <p style={styles.ref}>{language === 'nl' ? 'Referentie' : 'Reference'}: {property.ref}</p>
                )}
              </div>
              <p style={styles.price} data-testid="property-detail-price">{formatPrice(property.price)}</p>
            </div>

            <div style={styles.specs}>
              <div style={styles.specItem}>
                <Bed size={24} color="#C5A059" />
                <div>
                  <p style={styles.specLabel}>{t('property.bedrooms')}</p>
                  <p style={styles.specValue}>{property.bedrooms}</p>
                </div>
              </div>
              <div style={styles.specItem}>
                <Bath size={24} color="#C5A059" />
                <div>
                  <p style={styles.specLabel}>{t('property.bathrooms')}</p>
                  <p style={styles.specValue}>{property.bathrooms}</p>
                </div>
              </div>
              <div style={styles.specItem}>
                <Maximize size={24} color="#C5A059" />
                <div>
                  <p style={styles.specLabel}>{t('property.surface')}</p>
                  <p style={styles.specValue}>{property.surface}m²</p>
                </div>
              </div>
            </div>

            <div style={styles.tags}>
              <span style={styles.tag}>{property.property_type}</span>
              {property.new_build && <span style={{ ...styles.tag, ...styles.tagAccent }}>{t('property.newBuild')}</span>}
              {property.pool && <span style={styles.tag}>{language === 'nl' ? 'Zwembad' : 'Pool'}</span>}
            </div>

            {/* Prominent mid-content CTA — visible after only ~2 mobile scrolls.
                Production analytics showed the sidebar form sat 4 viewports
                down, killing conversion. */}
            <InlineCTA property={property} onOpen={openModal} />

            <MortgageCalculator price={property.price} />

            <div style={styles.description}>
              <h2 style={styles.descriptionTitle}>{t('property.description')}</h2>
              <p style={styles.descriptionText}>{property.description}</p>
            </div>

            {property.features && property.features.length > 0 && (
              <div style={styles.featuresSection}>
                <h2 style={styles.descriptionTitle}>
                  {language === 'nl' ? 'Kenmerken' : 'Features'}
                </h2>
                <ul style={styles.featuresList}>
                  {property.features.map((f, i) => (
                    <li key={i} style={styles.featureItem}>
                      <CheckCircle2 size={16} color="#C5A059" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <aside style={styles.sidebar} data-testid="property-lead-sidebar">
            <PropertyLeadForm property={property} defaultCta="info" />
          </aside>
        </div>

        {/* Bottom prev/next — the primary continuation UX after the visitor
            has consumed the description + specs. Repeats the top controls so
            long detail pages don't force a scroll back to the top. */}
        {hasListContext && filteredIds.length > 1 && (
          <BottomPrevNext
            prevId={prevId}
            nextId={nextId}
            currentIndex={currentIndex}
            total={filteredIds.length}
            isNL={language === 'nl'}
            onPrev={() => prevId && navigate(`/woning/${prevId}`)}
            onNext={() => nextId && navigate(`/woning/${nextId}`)}
            onBack={backToResults}
          />
        )}
      </div>

      {/* Sticky bottom CTA — always visible while scrolling. Driving the 200+
          mobile visitors who currently bounce before reaching the sidebar form. */}
      <StickyCTA property={property} onOpen={openModal} />

      <LeadModal
        open={modalCta !== null}
        onClose={closeModal}
        property={property}
        defaultCta={modalCta || 'info'}
      />
    </div>
  );
};

const styles = {
  page: { minHeight: '100vh', paddingTop: '2rem', paddingBottom: 'calc(4rem + 64px)' },
  container: { maxWidth: '1400px', margin: '0 auto', padding: '0 1.5rem' },

  topNavRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap',
  },
  backButton: {
    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
    backgroundColor: '#1A1918', color: '#C5A059',
    border: '1px solid #1A1918',
    padding: '0.6rem 1rem',
    fontSize: '0.85rem', fontWeight: 600,
    letterSpacing: '0.04em', textTransform: 'uppercase',
    fontFamily: "'Manrope', sans-serif",
    cursor: 'pointer',
  },
  backPosition: {
    marginLeft: '0.5rem', paddingLeft: '0.6rem', borderLeft: '1px solid rgba(197,160,89,0.4)',
    color: 'rgba(197,160,89,0.75)', fontSize: '0.75rem', fontWeight: 500, letterSpacing: '0.02em',
  },
  prevNext: { display: 'flex', gap: '0.4rem' },
  prevNextBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
    backgroundColor: 'white', color: '#1A1918', border: '1px solid #EAE5DD',
    padding: '0.55rem 0.85rem', fontSize: '0.82rem', fontWeight: 600,
    fontFamily: "'Manrope', sans-serif", cursor: 'pointer',
  },
  prevNextBtnDisabled: {
    display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
    backgroundColor: '#FAF6EE', color: '#C5C5C5', border: '1px solid #EAE5DD',
    padding: '0.55rem 0.85rem', fontSize: '0.82rem', fontWeight: 600,
    fontFamily: "'Manrope', sans-serif", cursor: 'not-allowed',
  },
  prevNextLabel: { display: 'inline' },

  backLink: {
    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
    color: '#585755', textDecoration: 'none', marginBottom: '1.5rem',
    fontSize: '0.9rem',
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 420px)',
    gap: '3rem',
    marginTop: '2rem',
  },
  main: { minWidth: 0 },
  sidebar: { position: 'sticky', top: '2rem', alignSelf: 'start' },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem',
  },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
    color: '#1A1918',
    marginBottom: '0.75rem',
    lineHeight: 1.2,
  },
  locationRow: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  location: { color: '#8C8B88', fontSize: '1rem' },
  ref: { color: '#8C8B88', fontSize: '0.85rem', marginTop: '0.5rem' },
  price: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(1.75rem, 3vw, 2.25rem)',
    color: '#C5A059',
    fontWeight: 500,
  },
  specs: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '2rem', padding: '2rem 0',
    borderTop: '1px solid #EAE5DD', borderBottom: '1px solid #EAE5DD',
    marginBottom: '2rem',
  },
  specItem: { display: 'flex', alignItems: 'center', gap: '1rem' },
  specLabel: { color: '#8C8B88', fontSize: '0.85rem', marginBottom: '0.25rem' },
  specValue: { color: '#1A1918', fontSize: '1.25rem', fontWeight: 500 },
  tags: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '2rem' },
  tag: {
    display: 'inline-block',
    backgroundColor: '#E6DCCA',
    color: '#585755',
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  tagAccent: { backgroundColor: '#1A1918', color: 'white' },
  description: { marginBottom: '2.5rem' },
  descriptionTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.4rem',
    color: '#1A1918',
    marginBottom: '1rem',
  },
  descriptionText: { color: '#585755', fontSize: '1rem', lineHeight: 1.8, whiteSpace: 'pre-line' },
  featuresSection: { marginBottom: '2.5rem' },
  featuresList: {
    listStyle: 'none', padding: 0, margin: 0,
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem',
  },
  featureItem: {
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    color: '#585755', fontSize: '0.9rem',
  },
  loadingText: { textAlign: 'center', color: '#8C8B88', padding: '4rem 0' },
  errorText: { textAlign: 'center', color: '#8C8B88', padding: '3rem 0', marginBottom: '2rem' },
};

// Mobile: stack
if (typeof window !== 'undefined' && window.innerWidth < 968) {
  styles.layout.gridTemplateColumns = '1fr';
  styles.sidebar.position = 'static';
}

/**
 * Style set for the bottom prev/next continuation bar. Kept in its own
 * object so the injected @media query below can target it precisely without
 * clashing with the top-nav styles.
 */
const bpn = {
  wrap: {
    marginTop: '3rem',
    padding: '1.25rem',
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    gap: '0.75rem',
    alignItems: 'stretch',
  },
  side: {
    display: 'flex', alignItems: 'center', gap: '0.75rem',
    backgroundColor: 'white', border: '1px solid #EAE5DD',
    color: '#1A1918', padding: '1rem 1.15rem',
    cursor: 'pointer', fontFamily: "'Manrope', sans-serif",
    textAlign: 'left', minHeight: '58px',
    transition: 'all 0.2s ease',
  },
  sideDisabled: {
    display: 'flex', alignItems: 'center', gap: '0.75rem',
    backgroundColor: '#FAF6EE', border: '1px solid #EAE5DD',
    color: '#C5C5C5', padding: '1rem 1.15rem',
    cursor: 'not-allowed', fontFamily: "'Manrope', sans-serif",
    textAlign: 'left', minHeight: '58px',
  },
  sideText: { display: 'flex', flexDirection: 'column', gap: '0.15rem', flex: 1, minWidth: 0 },
  sideLabel: { fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#C5A059' },
  sideId: { fontSize: '0.78rem', color: '#8C8B88' },
  center: {
    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
    backgroundColor: '#1A1918', color: '#C5A059',
    border: '1px solid #1A1918', padding: '1rem 1.5rem',
    fontSize: '0.82rem', fontWeight: 700,
    letterSpacing: '0.08em', textTransform: 'uppercase',
    fontFamily: "'Manrope', sans-serif", cursor: 'pointer',
  },
  centerCounter: {
    marginLeft: '0.5rem', paddingLeft: '0.6rem',
    borderLeft: '1px solid rgba(197,160,89,0.35)',
    color: 'rgba(197,160,89,0.75)', fontSize: '0.72rem', fontWeight: 500,
  },
};

/* Bottom bar stacks vertically on mobile so each control gets full tap width. */
if (typeof document !== 'undefined' && !document.getElementById('prev-next-bpn-mq')) {
  const s = document.createElement('style');
  s.id = 'prev-next-bpn-mq';
  s.textContent = `
    @media (max-width: 720px) {
      [data-testid="property-prev-next-bottom"] {
        grid-template-columns: 1fr !important;
      }
      [data-testid="property-prev-next-bottom"] [data-testid="property-back-bottom"] {
        order: -1;
      }
    }
  `;
  document.head.appendChild(s);
}

/* Hide the "Vorige/Volgende" text label on narrow phones so the button row
   stays comfortably below the back button on one line. Injected once. */
if (typeof document !== 'undefined' && !document.getElementById('prev-next-mq')) {
  const s = document.createElement('style');
  s.id = 'prev-next-mq';
  s.textContent = `
    @media (max-width: 480px) {
      [data-testid="property-prev-next"] [style*="display: inline"] { display: none !important; }
      [data-testid="property-prev-next"] button { padding: 0.55rem 0.7rem !important; }
    }
  `;
  document.head.appendChild(s);
}

export default PropertyDetail;
